/**
 * @deprecated This component has been superseded by TranscriptionHistoryPage.tsx
 * 
 * TranscriptionHistoryPage provides an improved dictation history UI with:
 *   - Enhanced filtering and search
 *   - Session detail drawer
 *   - Better data presentation
 * 
 * This file is retained at route /transcriptions/legacy for backward compatibility.
 * Do not use in new code. Primary route is /transcriptions → TranscriptionHistoryPage.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import { fetchTranscriptionHistory } from '../store/transcriptionSlice';
import transcriptionService from '../services/transcriptionService';
import { format } from 'date-fns';

// Matches the store's history item shape
interface Transcription {
  id: number;
  text: string;
  createdAt: string;
  status?: string;
  sessionId?: string;
}


const TranscriptionList: React.FC = () => {
  const { history, loading } = useAppSelector(state => state.transcription);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Update the type from any to Transcription
  const [selectedTranscription, setSelectedTranscription] = useState<Transcription | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchTranscriptionHistory());
  }, [dispatch]);

  // Update handler functions to use the Transcription type
  const handleViewTranscription = (transcription: Transcription) => {
    setSelectedTranscription(transcription);
    setIsViewDialogOpen(true);
  };

  const handleEditTranscription = (transcription: Transcription) => {
    setSelectedTranscription(transcription);
    setEditedText(transcription.text);
    setIsEditDialogOpen(true);
  };

  const handleDeleteTranscription = (transcription: Transcription) => {
    setSelectedTranscription(transcription);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (selectedTranscription && editedText) {
      setIsSubmitting(true);
      try {
        await transcriptionService.updateTranscription(selectedTranscription.id, editedText);
        dispatch(fetchTranscriptionHistory());
        setIsEditDialogOpen(false);
      } catch (error) {
        console.error('Failed to update transcription:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedTranscription) {
      setIsSubmitting(true);
      try {
        await transcriptionService.deleteTranscription(selectedTranscription.id);
        dispatch(fetchTranscriptionHistory());
        setIsDeleteDialogOpen(false);
      } catch (error) {
        console.error('Failed to delete transcription:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const truncateText = (text: string, maxLength = 100) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Transcription History
      </Typography>
      
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ py: 4 }}>
            No transcriptions found. Start by creating a new transcription.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Preview</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{formatDate(item.createdAt)}</TableCell>
                    <TableCell>{truncateText(item.text)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={item.status || 'Completed'} 
                        color={item.status === 'CORRECTED' ? 'success' : 'primary'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => item.sessionId
                          ? navigate(`/sessions/${item.sessionId}`)
                          : handleViewTranscription(item)
                        }
                        title={item.sessionId ? 'View session details' : 'View transcription'}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton onClick={() => handleEditTranscription(item)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteTranscription(item)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* View Dialog */}
      <Dialog
        open={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Transcription Details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Created: {selectedTranscription && formatDate(selectedTranscription.createdAt)}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 2, maxHeight: '60vh', overflow: 'auto' }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {selectedTranscription?.text}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          </DialogActions>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Transcription</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            multiline
            rows={15}
            fullWidth
            variant="outlined"
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)} disabled={isSubmitting}>Cancel</Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this transcription? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} disabled={isSubmitting}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained" 
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TranscriptionList;